import * as c2pService from '@api/company-person';
import { PersonPageDetails } from '@api/person';
import * as pintService from '@api/pint';
import { ColoredButton, ColoredChip, ColoredChipIconButton } from '@components/colored-elements';
import { IngestionMethodChip } from '@components/ingestion-method';
import { RelationSourceChip } from '@components/relation-source-chip';
import { AiOutlinePlusCircle, BiEdit, MdDelete } from '@icon/react-icons';
import { rbac, useToggle } from '@lib/common';
import { createReturnParam } from '@lib/deals';
import { Button, Dialog, DialogActions, DialogTitle, Grid, Tooltip } from '@material-ui/core';
import { blue, green, purple, red } from '@material-ui/core/colors';
import CopyClipboard from '@material-ui/icons/AttachFile';
import { useSnackbar } from 'notistack';
import { CellForLastModifiedAt } from 'pages/company/search/columns/utils';
import ClipBoard from 'pages/person-management/person-master-search/columns/utils';
import React, { PropsWithChildren, useCallback, useState } from 'react';
import { Link } from 'react-router-dom';
import { CellProps, Column } from 'react-table';
import styled from 'styled-components';
import {
  C2PIngestionMethod,
  C2PRelationSourceType,
  YesOrNoEnum,
} from '../company-person-relation.enums';
import {
  $empty,
  $eq,
  $in,
  DealFilter,
  MarketsResponsibleFilters,
  MarketsResponsiblePresenceFilters,
  StringFilters,
  withEnumMultiSelect,
  withMultiSelectAutoComplete,
} from '../filters';
import { $contains } from '../filters/operators';
import { useRefreshCounter } from '../relation-search/c2p-relation-search';
import {
  AddNonLinkedEntityFilter,
  addTitleToFilter,
  BooleanFilters,
  BooleanFiltersWithBoolValues,
  CellForHQCountry,
  createEnumMap,
  CurrentlyWorkingBooleanFilters,
  SingleBuyerSellerCell,
  YesAndNoFilters,
} from './utils';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import { renderEmailStatusIcon } from './utils';
import { StringFiltersDesignation } from '../filters/types/string-filters-designation';


type GlobalFilterColumn<D extends object = Record<string, any>> = Column<D> & {
  /**
   * Setting this to true will make the column come up in primary filters
   */
  primaryFilter?: boolean;
  /**
   * The operator to use with the primary filter. Use with `primaryFilter`
   */
  primaryFilterOperator?: string;
  /**
   * Force the columns to hide, this is usefull when you want to add a column
   * only to add the filter.
   */
  forceHide?: boolean;
  /**
   * A passive column is a column that can be used to filter, but
   * shows up in advanced filter.
   */
  passiveColumn?: boolean;
  overrideOperator?: { [k: string]: Partial<DealFilter> };
};

const TitledStringFilter = addTitleToFilter(StringFilters);
const EntityFilters = addTitleToFilter(AddNonLinkedEntityFilter(StringFilters));
const EntityFiltersDesignation = addTitleToFilter(AddNonLinkedEntityFilter(StringFiltersDesignation));


export const CellForCompEntity = () => (props: PropsWithChildren<CellProps<PersonPageDetails>>) => {
  const associated_comp: any = props?.row?.original;
  const RenderComponent = SingleBuyerSellerCell;

  if (!associated_comp?.company_name && !associated_comp?.company_vtr_id) {
    return null;
  }

  const firstEntityName = associated_comp?.company_name;
  return (
    <>
      <RenderComponent name={<>{firstEntityName} </>} vtrId={associated_comp?.company_vtr_id} />
    </>
  );
};

const YesOrNoChip = styled.div`
  padding: 10px;
  color: white;
  border-radius: 30px;
  width: fit-content;
`;

const CurrentlyWorkingNotLikeLabel = ({ label }) => (
  <>Currently working: Yes / Not sure</>
);

export const CurrentlyWorkingEntity =
  () => (props: PropsWithChildren<CellProps<PersonPageDetails>>) => {
    const original: any = props?.row?.original;

    const mapping = {
      [YesOrNoEnum.YES]: {
        icon: '✅',
        size: '20px',
        text: 'Yes',
      },
      [YesOrNoEnum.NO]: {
        icon: '❌',
        size: '18px',
        text: 'No',
      },
      [YesOrNoEnum.NOT_SURE]: {
        icon: '🤔',
        size: '20px',
        text: 'Not Sure',
      },
    };

    const currently_working_value = original?.currently_working;
    const icon = mapping[currently_working_value]?.icon || '';
    const size = mapping[currently_working_value]?.size;
    const text = mapping[currently_working_value]?.text || '';
    return (
      <Tooltip title={`Currently Working : ${text}`}>
        <YesOrNoChip style={{ fontSize: size }}>{icon}</YesOrNoChip>
      </Tooltip>
    );
  };

export const columns: GlobalFilterColumn<PersonPageDetails>[] = [
  {
    id: 'vtr_id',
    primaryFilter: true,
    primaryFilterOperator: $eq.key,
    Header: 'Vitrina Id',
    forceHide: true,
    disableFilters: true,
  },
  {
    id: 'company',
    primaryFilter: true,
    primaryFilterOperator: $contains.key,
    Header: 'Company',
    forceHide: true,
    disableFilters: true,
  },
  {
    id: 'person',
    accessor: row => row.person_name || 'NA',
    primaryFilter: true,
    primaryFilterOperator: $contains.key,
    Header: 'Name',
    disableFilters: true,
    Filter: EntityFilters,
    Cell: ({ row }) => {
      const { person_vtr_id = '', person_name = '' } = row?.original;
      return (
        <ClipBoard
          parentValue={person_name}
          icon={<CopyClipboard style={{ fontSize: '1.2em' }} color="primary" />}
          value={person_vtr_id ?? 'No Person VTR ID associated'}
        />
      );
    },
  },
  {
    id: 'name_search',
    accessor: row => row.designation || 'NA',
    primaryFilter: false,
    primaryFilterOperator: $contains.key,
    forceHide: true,
    Header: 'Person Name/ Company Name',
    Filter: EntityFilters,
  },
  {
    id: 'imdb_id',
    primaryFilter: true,
    primaryFilterOperator: $eq.key,
    Header: 'IMDB Id',
    forceHide: true,
  },
  {
    id: 'hubspot_id',
    primaryFilter: true,
    primaryFilterOperator: $eq.key,
    Header: 'Hubspot Id',
    forceHide: true,
    disableFilters: true,
  },
  {
    id: 'tmdb_id',
    primaryFilter: true,
    primaryFilterOperator: $eq.key,
    Header: 'TMDB Id',
    forceHide: true,
    disableFilters: true,
  },
  {
    id: 'hqcountry',
    primaryFilterOperator: $contains.key,
    Header: 'HQ country',
    Cell: CellForHQCountry,
    Filter: addTitleToFilter(
      withMultiSelectAutoComplete(pintService.getPintMasters, 'countries', 'name'),
      null,
      'Filter Companies by HQ Country'
    ),
    passiveColumn: true,
    forceHide: true,
  },
  {
    id: 'email',
    Header: 'Has Verified Email',
    Filter: addTitleToFilter(BooleanFiltersWithBoolValues, null, 'Filter on Verified Emails'),
    passiveColumn: true,
    forceHide: true,
  },
  {
    id: 'linkedin_url',
    Header: 'Has LinkedIn Url',
    Filter: addTitleToFilter(YesAndNoFilters, null, 'Filter on Person Linked In url.'),
    passiveColumn: true,
    forceHide: true,
  },
  {
    id: 'has_imdb_id',
    Header: 'Has IMDB Id',
    Filter: addTitleToFilter(YesAndNoFilters, null, 'Filter on Person IMDB Id.'),
    passiveColumn: true,
    forceHide: true,
  },
  {
    id: 'has_content',
    Header: 'Has Content',
    Filter: addTitleToFilter(BooleanFilters, null, 'Filter on Person content.'),
    passiveColumn: true,
    forceHide: true,
  },
  {
    id: 'has_deals',
    Header: 'Has Deals',
    Filter: addTitleToFilter(BooleanFilters, null, 'Filter on Person Deals.'),
    passiveColumn: true,
    forceHide: true,
  },
  {
    id: 'more_than_1',
    Header: 'Working for more than 1 company',
    Filter: addTitleToFilter(BooleanFilters, null, 'Filter on Person Deals.'),
    passiveColumn: true,
    forceHide: true,
  },
  {
    id: 'currently_working',
    Header: 'Currently working',
    Filter: addTitleToFilter(CurrentlyWorkingBooleanFilters, null, 'Filter on Person Deals.'),
    passiveColumn: true,
    forceHide: true,
    overrideOperator: {
    $notlike: {
      operatorLabel: 'is',
      ChipLabel: CurrentlyWorkingNotLikeLabel,
    },
  },
  },
  {
    id: 'markets_responsible',
    Header: 'Markets Responsible',
    Filter: addTitleToFilter(
      MarketsResponsibleFilters,
      null,
      'Filter by markets responsible.',
    ),
    passiveColumn: true,
    forceHide: true,
    disableSortBy: true,
    overrideOperator: {
      [$in.key]: {
        ChipLabel: ({ label, value }) => (
          <>{label}: {String(value).split(',').filter(Boolean).join(', ')}</>
        ),
      },
    },
  },
  {
    id: 'has_markets_responsible',
    Header: 'Has Markets Responsible',
    Filter: addTitleToFilter(
      MarketsResponsiblePresenceFilters,
      null,
      'Yes has data. No has no data.',
    ),
    passiveColumn: true,
    forceHide: true,
    disableSortBy: true,
    overrideOperator: {
      [$empty.key]: {
        ChipLabel: ({ label, value }) => (
          <>{label}: {String(value) === 'true' ? 'No - Has no data' : 'Yes - Has data'}</>
        ),
      },
    },
  },
  {
    id: 'designation',
    accessor: row => row.designation || 'NA',
    primaryFilter: false,
    primaryFilterOperator: $contains.key,
    Header: 'Designation',
    Filter: EntityFiltersDesignation,
    // minWidth: 150,
    // disableSortBy: true,
  },
  {
    accessor: row => row.deal_role || 'NA',
    primaryFilter: false,
    primaryFilterOperator: $contains.key,
    Header: 'Role in Deal',
    Filter: EntityFilters,
    // minWidth: 150,
    disableSortBy: true,
  },
  {
    Header: 'Company',
    Cell: CellForCompEntity(),
    // minWidth: 250,
    disableSortBy: false,
  },
  {
    accessor: row => row.currently_working || '',
    Header: 'Currently Working',
    disableFilters: true,
    disableSortBy: true,
    Cell: CurrentlyWorkingEntity(),
  },
  {
    Header: 'Email Available',
    Cell: (props) => {
   const original = props?.row?.original;
    return renderEmailStatusIcon({
      email: original?.email,
      isEmailVerified: original?.is_email_verified,
    });
    },
    disableSortBy: false,
    primaryFilter: false,
  },
  {
    id: 'source',
    accessor: row => row.source || 'NA',
    primaryFilter: false,
    primaryFilterOperator: $eq.key,
    Header: 'Sources',
    // Filter: EntityFilters,
    // minWidth: 150,
    disableSortBy: true,
    Cell: ({ value }) => <RelationSourceChip type={value as unknown as C2PRelationSourceType} />,
    Filter: addTitleToFilter(
      withEnumMultiSelect(createEnumMap(C2PRelationSourceType, true), true, true),
      null,
      'Filter Companies by Source'
    ),
    overrideOperator: {
      [$eq.key]: {
        ChipLabel: ({ label, value }) => (
          <>{`${label} one of ${value}
            `}</>
        ),
      },
    },
  },
  {
    id: 'ingestion_method',
    accessor: row => row.ingestion_method || 'NA',
    primaryFilter: false,
    primaryFilterOperator: $eq.key,
    Header: 'Ingestion method',
    // Filter: EntityFilters,
    // minWidth: 150,
    disableSortBy: true,
    Cell: ({ value }) => <IngestionMethodChip type={value as unknown as C2PIngestionMethod} />,
    Filter: addTitleToFilter(
      withEnumMultiSelect(createEnumMap(C2PIngestionMethod, true), true, true),
      null,
      'Filter Companies by Ingestion Method'
    ),
    overrideOperator: {
      [$eq.key]: {
        ChipLabel: ({ label, value }) => (
          <>{`${label} one of ${value}
            `}</>
        ),
      },
    },
  },
  {
    id: 'modified_at',
    accessor: row => row?.modified_at || 'NA',
    primaryFilter: false,
    primaryFilterOperator: $contains.key,
    Header: 'Modified at',
    Cell: CellForLastModifiedAt,
    Filter: EntityFilters,
    // minWidth: 150,
    disableFilters: true,
  },
  {
    id: 'modified_by',
    accessor: row => row.updated_by || 'NA',
    Header: 'Modified By',
    Cell: ({ value }) => (
      <Tooltip title={value}>
        <ColoredChip colored={blue[500]} size="small" label={value} />
      </Tooltip>
    ),
    disableFilters: true,
    primaryFilter: false,
  },
  {
    id: 'actions',
    minWidth: 100,
    // maxWidth: 80,
    Header: 'Actions',
    Cell({ row }) {
      const returnTo = createReturnParam('/company-person/search');
      const {
        person_company_relation_id = '',
        is_email_verified = false,
        currently_working = '',
        source = "Vitrina"
      } = row?.original;
      const disableTheDeleteBtn =  currently_working === 'yes';
      return (
         <StyledGrid container justify="center" alignItems="center">
          <IconContainer>
            <Tooltip title="Edit C2P">
              <ColoredChipIconButton
                component={Link}
                to={`/company-person/edit/${person_company_relation_id}${returnTo}`}
                pointer
                color="primary"
                size="medium"
                icon={<BiEdit />}
              />
            </Tooltip>
            <Tooltip title="Delete C2P Relation">
              <DeleteC2PRelationDialog
                personCompanyRelationId={person_company_relation_id}
                deleteDisable={disableTheDeleteBtn ? true : rbac('C2P_Delete', '', 'Button')}
                deleteC2P
              />
            </Tooltip>
          </IconContainer>
          {source !== 'DealIntel' && source !== 'GVD' ? (
            <Grid item xs container spacing={1}>
              <Grid item xs>
                <Tooltip title="Replicate Relation">
                  <ColoredChipIconButton
                    component={Link}
                    to={`/company-person/new?originalRelationId=${person_company_relation_id}`}
                    pointer
                    colored={purple[700]}
                    coloredHover={purple[900]}
                    size="medium"
                    icon={<FileCopyIcon />}
                  />
                </Tooltip>
              </Grid>
            </Grid>
          ) : null}
        </StyledGrid>
      );
    },
  },
];

export const DeleteC2PRelationDialog = React.forwardRef(
  (
    {
      buttonType = 'Icon',
      personCompanyRelationId,
      deleteDisable,
      deleteC2P = false,
      ...props
    }: any,
    ref
  ) => {
    const { forceRefresh } = useRefreshCounter();
    const { enqueueSnackbar, closeSnackbar } = useSnackbar();
    const [editOpen, , showEditDialog, hideEditDialog] = useToggle(false);
    const [editingServiceMaster, setEditingServiceMaster] = useState(false);

    const onDeleteFinish = useCallback(RelationId => {
      const infoNotification = enqueueSnackbar('Please wait deleting C2P', {
        variant: 'info',
      });
      return c2pService.deleteC2P({ person_company_rel_id: RelationId }).subscribe({
        next: () => {
          enqueueSnackbar('C2P deleted Successfully!', { variant: 'success' });
          // redirectToSearch();
          hideEditDialog();
          forceRefresh();
        },
        error: err => enqueueSnackbar(`Error deleting C2P (${err})`, { variant: 'error' }),
        complete: () => closeSnackbar(infoNotification),
      });
    }, []);

    return (
      <div ref={ref as React.RefObject<HTMLDivElement>}>
        {buttonType === 'Icon' ? (
          deleteC2P ? (
            <ColoredChipIconButton
              pointer
              onClick={showEditDialog}
              colored={red[700]}
              coloredHover={red[900]}
              size="medium"
              icon={<MdDelete />}
              disabled={deleteDisable}
              {...props} //Extra props are sent from tooltip to children
            />
          ) : (
            <ColoredChipIconButton
              pointer
              onClick={showEditDialog}
              // colored={blue[700]}
              // coloredHover={blue[900]}
              size="small"
              color="primary"
              icon={<BiEdit />}
              {...props} //Extra props are sent from tooltip to children
            />
          )
        ) : (
          <ColoredButton
            colored={green[700]}
            coloredHover={green[900]}
            startIcon={<AiOutlinePlusCircle />}
            component="button"
            onClick={showEditDialog}
            variant="contained">
            Add
          </ColoredButton>
        )}
        <C2PDeleteDialog
          personCompanyRelationId={personCompanyRelationId}
          onDeleteFinish={onDeleteFinish}
          deleteC2P
          open={editOpen}
          onClose={hideEditDialog}
        />
      </div>
    );
  }
);

export function C2PDeleteDialog({
  personCompanyRelationId,
  deleteC2P = false,
  onDeleteFinish = null,
  onClose = null,
  open,
}) {
  return deleteC2P ? (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description">
      <DialogTitle id="alert-dialog-title">
        Are you sure you want to delete this C2P Relation?
      </DialogTitle>
      <DialogActions>
        <Button onClick={onClose} color="primary">
          Cancel
        </Button>
        <Button onClick={() => onDeleteFinish(personCompanyRelationId)} color="primary" autoFocus>
          Ok
        </Button>
      </DialogActions>
    </Dialog>
  ) : null;
}
const IconContainer = styled.span`
  display: flex;
  gap: 8px;
  padding: 15px 10px;
  justify-content: center;
`;
const StyledGrid = styled(Grid)`
  padding: 5px 0;
  & .MuiGrid-grid-xs-true {
    flex-grow: 0;
  }
  & .MuiGrid-spacing-xs-1 {
    padding: 5px;
  }
`;